import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Trophy, Users, FileText, Loader2, Euro, CheckCircle2, Send } from "lucide-react";
import { api } from "@/lib/api";

const money = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/report/aggregate");
      setData(r.data);
    } catch (e) { toast.error("Errore caricamento report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return (
    <div className="p-10 text-sm text-[#0A0A0A]/50 flex items-center gap-2">
      <Loader2 size={16} className="animate-spin" /> Carico report...
    </div>
  );
  if (!data) return null;

  const { totals, per_rep, top } = data;
  const maxFatt = Math.max(1, ...per_rep.map((r) => r.fatturato_firmato));

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-report">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <BarChart3 size={14} /> Vista Manager
        </div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Report vendite</h1>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">Overview di tutta la squadra vendite: contratti firmati, fatturato e provvigioni maturate per venditore.</p>
      </header>

      {/* KPI totali */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KpiCard icon={Users} label="Venditori" value={totals.venditori} />
        <KpiCard icon={FileText} label="Contratti totali" value={totals.contratti_totali} />
        <KpiCard icon={CheckCircle2} label="Firmati" value={totals.contratti_firmati} accent="green" />
        <KpiCard icon={Euro} label="Fatturato" value={money(totals.fatturato_firmato)} accent="orange" />
        <KpiCard icon={TrendingUp} label="Provvigioni" value={money(totals.provvigioni_maturate)} accent="orange" />
      </div>

      {/* Top venditore */}
      {top && top.fatturato_firmato > 0 && (
        <div className="mb-8 border-2 border-[#FF5A1F] bg-gradient-to-r from-[#FFF5EF] to-[#FAFAF7] p-5 flex items-center gap-4" data-testid="top-seller">
          <div className="w-16 h-16 rounded-full bg-[#FF5A1F] text-white flex items-center justify-center">
            <Trophy size={32} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A1F] font-bold">Top venditore</div>
            <div className="font-display text-3xl uppercase text-[#0A0A0A]">{top.name}</div>
            <div className="text-sm text-[#0A0A0A]/60 mt-1">
              {money(top.fatturato_firmato)} fatturato · {top.contratti_firmati} contratti firmati · {money(top.provvigioni_maturate)} di provvigioni
            </div>
          </div>
        </div>
      )}

      {/* Tabella per venditore */}
      <div className="border-2 border-[#0A0A0A]">
        <div className="bg-[#0A0A0A] text-white p-4 flex items-center gap-2">
          <BarChart3 size={16} />
          <div className="font-display text-xl uppercase">Per venditore</div>
        </div>
        {per_rep.length === 0 ? (
          <div className="p-8 text-center text-[#0A0A0A]/50 text-sm">Nessun venditore ancora attivo</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F2F2EC]">
              <tr className="text-left">
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold">Venditore</th>
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold text-center">Contratti</th>
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold text-center">Firmati</th>
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold text-center">Inviati</th>
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold text-right">Fatturato</th>
                <th className="p-3 text-[10px] uppercase tracking-widest font-bold text-right">Provvigioni</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {per_rep.map((r, idx) => (
                <tr key={r.id} className="border-t border-[#0A0A0A]/10 hover:bg-[#FAFAF7]" data-testid={`report-row-${r.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#FF5A1F] text-white flex items-center justify-center text-xs font-bold uppercase">
                        {(r.name || "?").slice(0, 1)}
                      </div>
                      <div>
                        <div className="font-bold text-[#0A0A0A]">{r.name}</div>
                        <div className="text-[10px] text-[#0A0A0A]/50">{r.email}</div>
                      </div>
                      {idx === 0 && r.fatturato_firmato > 0 && <Trophy size={14} className="text-[#FF5A1F]" />}
                    </div>
                  </td>
                  <td className="p-3 text-center font-mono">{r.contratti_totali}</td>
                  <td className="p-3 text-center font-mono text-green-700 font-bold">{r.contratti_firmati}</td>
                  <td className="p-3 text-center font-mono text-blue-700">{r.contratti_inviati}</td>
                  <td className="p-3 text-right font-mono font-bold">{money(r.fatturato_firmato)}</td>
                  <td className="p-3 text-right font-mono font-bold text-[#FF5A1F]">{money(r.provvigioni_maturate)}</td>
                  <td className="p-3">
                    <div className="w-24 h-2 bg-[#0A0A0A]/10 rounded" title={`${((r.fatturato_firmato / maxFatt) * 100).toFixed(0)}% del top`}>
                      <div className="h-full bg-[#FF5A1F]" style={{ width: `${(r.fatturato_firmato / maxFatt) * 100}%` }}></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  const cls = accent === "green" ? "text-green-700" : accent === "orange" ? "text-[#FF5A1F]" : "text-[#0A0A0A]";
  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold">
        <Icon size={12} /> {label}
      </div>
      <div className={`mt-2 font-display text-3xl ${cls}`}>{value}</div>
    </div>
  );
}
